/** @jsxImportSource @emotion/react */
import Typography from '@mui/material/Typography'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Field, Form as FinalForm } from 'react-final-form'
import createDecorator from 'final-form-focus'
import { OnChange } from 'react-final-form-listeners'
import arrayMutators from 'final-form-arrays'
import omit from 'lodash/omit'
import debounce from 'lodash/debounce'
import sortBy from 'lodash/sortBy'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { QUESTION_MEDIA_PATH_S3 } from 'constants/images'
import { DEBOUNCE_DELAY, PlatformSlug } from 'constants/consts'
import {
  findOneQuestion_findOneQuestion as Question,
  findOneQuestion_findOneQuestion_questionCategory as QuestionCategory,
} from '__generatedTypes__/findOneQuestion'
import { findQuestionLocation_findQuestionLocation_location as QuestionLocation } from '__generatedTypes__/findQuestionLocation'
import { CreateQuestionInput, QuestionInvertTypeEnum, QuestionTypeEnum } from '__generatedTypes__/globalTypes'
import { InputField } from 'components/Input'
import { SelectField } from 'components/Select'
import { MultiSelectField } from 'components/MultiSelect'
import { EditorField, prepareEditorHtml } from 'components/Editor'
import Badge from 'components/Badge'
import { Revision, Revisions } from 'components/Revisions'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { validateRequired, validateRequiredHtml } from 'utils/validation'
import { theme } from 'theme/theme'
import { CourseName } from 'theme/utils'
import { isImageUploadError, loadImageFiles, uploadImageFiles } from 'utils/imageFile'
import { setFieldData } from 'utils/setFieldData'
import { PageLoader } from 'components/PageLoader'
import { getFullPlatformTitle } from 'components/Layout/Sidebar/PlatformList'
import { toast } from 'utils/toast'
import * as classes from './styles'
import * as commonClasses from './commonStyles'
import TemplateForm from './TemplateForm'
import RemoveButton from './RemoveButton'
import { InvertImagesField } from './InvertImagesField'
import {
  FormState,
  QUESTION_TYPES,
  QuestionType,
  DEFAULT_ANSWERS,
  getAdditionalFieldsConfig,
  FormStateApi,
  SOURCES_AND_CREDITS_CONFIG,
} from './consts'
import { AdditionalFields } from './AdditionalFields'
import { AdditionalField } from './AdditionalFields/AdditionalField'
import { getInitialTableFields, getTableFieldsForSubmit } from './AdditionalFields/TableFields/tableFieldHelpers'
import { OptionChoices } from './OptionChoices'
import { RxWritingAnswers } from './RxWritingAnswers'
import { NumericResponseAnswer } from './NumericResponseAnswer'
import { TextCompletionAnswers } from './TextCompletionAnswers'
import { useAnswerChange } from './hooks'
import { Platform, SubjectGroup, useQuestionHelpers } from './useQuestionHelpers'
import { getQuestionTypeConfig } from './questionTypeConfig'
import { prepareAnswers } from './prepareAnswers'
import { parseAnswers } from './parseAnswers'

const INVERT_IMAGES_SUBJECT_GROUP_TITLES = [
  CourseName.GeneralChemistry,
  CourseName.OrganicChemistry,
  CourseName.QuantitativeReasoning,
]

const DEFAULT_QUESTION_STEM =
  'Write a prescription for the most appropriate antibiotic for this patient, using the generic name of the drug.'

export type Props = {
  question?: Question
  onSubmit: (question: CreateQuestionInput, formApi: FormStateApi) => Promise<void>
  questionLocations?: QuestionLocation[]
  disableDeleteButton?: boolean
  isTemplate?: boolean
}

const focusOnErrors = createDecorator<FormState, Partial<FormState>>()

export const getInitialValues = async (
  question?: Question,
  template?: Question,
  defaultQuestionType?: QuestionTypeEnum,
  subjectGroups?: SubjectGroup[]
): Promise<Partial<FormState>> => {
  const questionData = template ?? question
  const questionType = questionData?.type ?? defaultQuestionType ?? QuestionTypeEnum.SingleChoice

  let additionalFields = {}
  const additionalFieldsConfig = getAdditionalFieldsConfig(questionType)

  // additional INBDE/NBDHE/NDEB fields:
  if (additionalFieldsConfig) {
    const [exhibitFile, xRayFile, photoFile, periodontalChartFile, panoramicChartFile, dentalChartFile] =
      await Promise.all([
        loadImageFiles(questionData?.exhibit),
        loadImageFiles(questionData?.xRay),
        loadImageFiles(questionData?.photo),
        loadImageFiles(questionData?.periodontalChart),
        loadImageFiles(questionData?.panoramicChart),
        loadImageFiles(questionData?.dentalChart),
      ])

    additionalFields = {
      exhibitFile,
      xRayFile,
      photoFile,
      periodontalChartFile,
      panoramicChartFile,
      dentalChartFile,
      model3D: questionData?.model3D,
      isNeedAdditionalInformation: !!questionData?.additionalInformation,
      isNeedExhibitFile: !!exhibitFile,
      isNeedXRayFile: !!xRayFile,
      isNeedPhotoFile: !!photoFile,
      isNeedDentalChartFile: !!dentalChartFile,
      isNeedPeriodontalChartFile: !!periodontalChartFile,
      isNeedPanoramicChartFile: !!panoramicChartFile,
      isNeedModel3D: !!questionData?.model3D,
      showMultipleSelectionLabel: questionData?.showMultipleSelectionLabel,
      ...getInitialTableFields(additionalFieldsConfig.tableFields, questionData),
      isNeedPatientBox: Boolean(
        (!questionData && additionalFieldsConfig.patientBoxNeededByDefault) || // show Patient Box by default for new questions
          questionData?.type === QuestionTypeEnum.RxWritingNDEB || // always show Patient Box for RxWritingNDEB
          questionData?.patient ||
          questionData?.chiefComplaint ||
          questionData?.backgroundAndOrPatientHistory ||
          questionData?.dentalHistory ||
          questionData?.medicalHistory ||
          questionData?.currentFinding
      ),
    }
  }

  const answers: Question['answer'] | undefined =
    (template?.answer?.map((answer) => omit(answer, 'id')) as Question['answer']) ?? question?.answer

  const getInitialSubjectGroup = () => {
    if (!questionData?.subjects?.length) {
      return undefined
    }

    if (!subjectGroups?.length) {
      // This fake subject group is used only for clone question functionality, where we call getInitialValues and prepareForSubmit based on original question data
      return {
        id: 'fake-subject-group',
        slug: 'Fake subject group',
        title: 'Fake subject group',
        subjects: questionData.subjects.map((subject) => ({
          ...subject,
          platformSlug: subject.platform.slug as PlatformSlug,
        })),
      } as unknown as SubjectGroup
    }

    const selectedSubjectGroup = subjectGroups.find((subjectGroup) =>
      questionData.subjects.every(({ id }) => subjectGroup.subjects.some((subject) => subject.id === id))
    )

    if (!selectedSubjectGroup) {
      return undefined
    }

    // Keep only subjects for selected platforms
    return {
      ...selectedSubjectGroup,
      subjects: selectedSubjectGroup.subjects.filter((subject) =>
        questionData.subjects.some(({ id }) => id === subject.id)
      ),
    }
  }

  const selectedSubjectGroup = getInitialSubjectGroup()

  return {
    ...(questionData ?? {}),
    questionStem: questionData?.questionStem ?? DEFAULT_QUESTION_STEM,
    resetStatistics: false,
    isTemplate: question?.isTemplate,
    history: question?.history ?? [],
    answer: (answers ? sortBy(answers, 'order') : DEFAULT_ANSWERS).map(({ point, ...answer }) => ({
      ...answer,
      point: point && Math.abs(+point),
    })),
    ...parseAnswers(questionType, sortBy(answers, 'order')),
    platforms: questionData?.subjects?.map(({ platform }) => platform) ?? [],
    subjectGroup: selectedSubjectGroup,
    type: questionType,
    questionType: QUESTION_TYPES.find(({ id }) => id === questionType),
    invertQuestionImages: questionData?.invertQuestionImages ?? QuestionInvertTypeEnum.AUTO,
    invertAnswerImages: questionData?.invertAnswerImages ?? QuestionInvertTypeEnum.AUTO,
    invertAnswerExplanationImages: questionData?.invertAnswerExplanationImages ?? QuestionInvertTypeEnum.AUTO,
    ...additionalFields,
    sourcesAndCredits: questionData?.sourcesAndCredits ?? '',
    isNeedSourcesAndCredits: !!questionData?.sourcesAndCredits,
  }
}

export const prepareForSubmit = async (
  values: Partial<FormState>,
  templateName?: string
): Promise<CreateQuestionInput> => {
  const type = values.type ?? QuestionTypeEnum.SingleChoice

  const [
    exhibit,
    xRay,
    photo,
    periodontalChart,
    panoramicChart,
    dentalChart,
    questionAsHtml,
    testletScenario,
    answerExplanationAsHtml,
    sourcesAndCreditsAsHtml,
    additionalInformationAsHtml,
    patientBoxFields,
    formattedAnswers,
  ] = await Promise.all([
    uploadImageFiles(values.exhibitFile, QUESTION_MEDIA_PATH_S3, true),
    uploadImageFiles(values.xRayFile, QUESTION_MEDIA_PATH_S3, true),
    uploadImageFiles(values.photoFile, QUESTION_MEDIA_PATH_S3, true),
    uploadImageFiles(values.periodontalChartFile, QUESTION_MEDIA_PATH_S3, true),
    uploadImageFiles(values.panoramicChartFile, QUESTION_MEDIA_PATH_S3, true),
    uploadImageFiles(values.dentalChartFile, QUESTION_MEDIA_PATH_S3, true),
    prepareEditorHtml(values.questionAsHtml ?? '', QUESTION_MEDIA_PATH_S3),
    prepareEditorHtml(values.testletScenario ?? '', QUESTION_MEDIA_PATH_S3),
    prepareEditorHtml(values.answerExplanationAsHtml ?? '', QUESTION_MEDIA_PATH_S3),
    values.isNeedSourcesAndCredits ? prepareEditorHtml(values.sourcesAndCredits ?? '', QUESTION_MEDIA_PATH_S3) : null,
    values.isNeedAdditionalInformation
      ? prepareEditorHtml(values.additionalInformation ?? '', QUESTION_MEDIA_PATH_S3)
      : null,
    Promise.all(
      values.isNeedPatientBox
        ? [
            prepareEditorHtml(values.patient ?? '', QUESTION_MEDIA_PATH_S3),
            prepareEditorHtml(values.chiefComplaint ?? '', QUESTION_MEDIA_PATH_S3),
            prepareEditorHtml(values.backgroundAndOrPatientHistory ?? '', QUESTION_MEDIA_PATH_S3),
            prepareEditorHtml(values.currentFinding ?? '', QUESTION_MEDIA_PATH_S3),
            prepareEditorHtml(values.dentalHistory ?? '', QUESTION_MEDIA_PATH_S3),
            prepareEditorHtml(values.medicalHistory ?? '', QUESTION_MEDIA_PATH_S3),
          ]
        : []
    ),
    prepareAnswers(values, type),
  ])

  return {
    answer: templateName ? formattedAnswers.map((answer) => omit(answer, 'id')) : formattedAnswers,
    answerExplanationAsHtml,
    sourcesAndCredits: sourcesAndCreditsAsHtml,
    history: templateName ? [] : values.history?.map(({ updatedAt, updatedBy }) => ({ updatedAt, updatedBy })) ?? [],
    isTemplate: templateName ? true : values.isTemplate ?? false,
    templateName: values.templateName || templateName || undefined,
    questionAsHtml,
    testletScenario,
    questionStem: values.questionStem ?? '',
    questionCategory: values.questionCategory?.id
      ? {
          id: values.questionCategory.id,
        }
      : undefined,
    resetStatistics: templateName ? false : values.resetStatistics ?? false,
    subjects: values.subjectGroup?.subjects?.map(({ id }) => ({ id })) ?? [],
    title: values.title ?? '',
    type,
    invertQuestionImages: values.invertQuestionImages,
    invertAnswerImages: values.invertAnswerImages,
    invertAnswerExplanationImages: values.invertAnswerExplanationImages,
    // additional INBDE/NBDHE/NDEB fields:
    additionalInformation: additionalInformationAsHtml,
    exhibit: values.isNeedExhibitFile ? exhibit : null,
    xRay: values.isNeedXRayFile ? xRay : null,
    photo: values.isNeedPhotoFile ? photo : null,
    patient: values.isNeedPatientBox ? patientBoxFields?.[0] ?? '' : null,
    periodontalChart: values.isNeedPeriodontalChartFile ? periodontalChart : null,
    panoramicChart: values.isNeedPanoramicChartFile ? panoramicChart : null,
    dentalChart: values.isNeedDentalChartFile ? dentalChart : null,
    chiefComplaint: values.isNeedPatientBox ? patientBoxFields?.[1] ?? '' : null,
    backgroundAndOrPatientHistory: values.isNeedPatientBox ? patientBoxFields?.[2] ?? '' : null,
    currentFinding: values.isNeedPatientBox ? patientBoxFields?.[3] ?? '' : null,
    dentalHistory: values.isNeedPatientBox ? patientBoxFields?.[4] ?? '' : null,
    medicalHistory: values.isNeedPatientBox ? patientBoxFields?.[5] ?? '' : null,
    model3D: values.isNeedModel3D ? values.model3D ?? '' : null,
    ...getTableFieldsForSubmit(getAdditionalFieldsConfig(type)?.tableFields ?? [], values),
    showMultipleSelectionLabel: values.showMultipleSelectionLabel,
  }
}

export const Form = ({
  question,
  onSubmit,
  questionLocations,
  disableDeleteButton = false,
  isTemplate = false,
}: Props) => {
  const { platform } = useParams()
  const currentPlatformSlug = platform ? (String(platform) as PlatformSlug) : undefined
  const [submitting, setSubmitting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [initialValues, setInitialValues] = useState<Partial<FormState> | undefined>()
  const [isResetStatisticsDisabled, setIsResetStatisticsDisabled] = useState(false)
  const [removeError, setRemoveError] = useState<boolean>(false)

  const {
    loading,
    allSubjectGroups,
    getAvailableQuestionTypes,
    getAvailablePlatforms,
    getAvailableSubjectGroups,
    getAvailableQuestionCategories,
  } = useQuestionHelpers()
  const [availableQuestionTypes, setAvailableQuestionTypes] = useState<QuestionType[]>([])
  const [availablePlatforms, setAvailablePlatforms] = useState<Platform[]>([])
  const [availableSubjectGroups, setAvailableSubjectGroups] = useState<SubjectGroup[]>([])
  const [availableQuestionCategories, setAvailableQuestionCategories] = useState<QuestionCategory[]>([])

  const { initialCorrectAnswerIds, onAnswerChange } = useAnswerChange(setIsResetStatisticsDisabled, question)

  const resetForm = useCallback(
    (initialValues: Partial<FormState>) => {
      const initialValuesCopy = { ...initialValues }
      if (initialValuesCopy.id) {
        setAvailableQuestionTypes(getAvailableQuestionTypes(question ? undefined : currentPlatformSlug))
        setAvailablePlatforms(getAvailablePlatforms(initialValuesCopy.type))
        setAvailableSubjectGroups(
          getAvailableSubjectGroups(
            initialValuesCopy.platforms?.map((platform) => platform.slug as PlatformSlug),
            initialValuesCopy.type,
            true
          )
        )
        setAvailableQuestionCategories(
          getAvailableQuestionCategories(initialValuesCopy.subjectGroup?.subjects?.map((subject) => subject.id))
        )
      } else {
        setAvailableQuestionTypes(getAvailableQuestionTypes(currentPlatformSlug))
        const initialAvailablePlatforms = getAvailablePlatforms(initialValuesCopy.type)
        const initialSelectedPlatform =
          initialAvailablePlatforms.length === 1 ? initialAvailablePlatforms[0] : undefined
        if (initialSelectedPlatform) {
          initialValuesCopy.platforms = [initialSelectedPlatform]
        }
        setAvailablePlatforms(initialAvailablePlatforms)
        const initialSelectedSubjectGroups = getAvailableSubjectGroups(
          initialSelectedPlatform ? [initialSelectedPlatform.slug as PlatformSlug] : undefined,
          initialValuesCopy.type,
          true
        )
        const initialSelectedSubjectGroup =
          initialSelectedSubjectGroups.length === 1 ? initialSelectedSubjectGroups[0] : undefined
        if (initialSelectedSubjectGroup) {
          initialValuesCopy.subjectGroup = initialSelectedSubjectGroup
        }
        setAvailableSubjectGroups(initialSelectedSubjectGroups)
        const initialAvailableQuestionCategories = getAvailableQuestionCategories(
          initialSelectedSubjectGroup?.subjects?.map((subject) => subject.id)
        )
        setAvailableQuestionCategories(initialAvailableQuestionCategories)
      }

      setInitialValues(initialValuesCopy)
      setRemoveError(false)
    },
    [
      question,
      getAvailableQuestionTypes,
      getAvailablePlatforms,
      getAvailableSubjectGroups,
      getAvailableQuestionCategories,
      currentPlatformSlug,
    ]
  )

  useEffect(() => {
    if (loading) {
      return
    }

    ;(async () => {
      const defaultQuestionType = getAvailableQuestionTypes(currentPlatformSlug)?.[0]?.id
      const initialValues = await getInitialValues(question, undefined, defaultQuestionType, allSubjectGroups)
      resetForm(initialValues)
    })()
  }, [question, loading])

  const handleQuestionTypeChange = useCallback(
    (questionType: QuestionTypeEnum, form: FormStateApi) => {
      const newPlatforms = getAvailablePlatforms(questionType)
      setAvailablePlatforms(newPlatforms)

      if (newPlatforms?.length === 1) {
        form.change('platforms', newPlatforms)
        return
      }

      const selectedPlatforms = form.getFieldState('platforms')?.value
      const newSelectedPlatforms = selectedPlatforms
        ? selectedPlatforms.filter((platform) => newPlatforms.some((newPlatform) => newPlatform.id === platform.id))
        : []
      form.change('platforms', newSelectedPlatforms)
    },
    [getAvailablePlatforms]
  )

  const handlePlatformsChange = useCallback(
    (platforms: Platform[], form: FormStateApi) => {
      const type = form.getFieldState('type')?.value
      const newSubjectGroups = getAvailableSubjectGroups(
        platforms.map((platform) => platform.slug as PlatformSlug),
        type,
        true
      )
      setAvailableSubjectGroups(newSubjectGroups)

      if (newSubjectGroups?.length === 1) {
        form.change('subjectGroup', newSubjectGroups[0])
        return
      }

      const selectedSubjectGroupId = form.getFieldState('subjectGroup')?.value?.id
      const newSelectedSubjectGroup = selectedSubjectGroupId
        ? newSubjectGroups.find((subjectGroup) => subjectGroup.id === selectedSubjectGroupId)
        : undefined
      form.change('subjectGroup', newSelectedSubjectGroup)
    },
    [getAvailableSubjectGroups]
  )

  const handleSubjectChange = useCallback(
    (subjectGroup: SubjectGroup | undefined, form: FormStateApi) => {
      const newQuestionCategories = getAvailableQuestionCategories(
        subjectGroup?.subjects?.map(({ id }) => id) ?? undefined
      )
      setAvailableQuestionCategories(newQuestionCategories)

      const selectedQuestionCategoryId = form.getFieldState('questionCategory')?.value?.id
      const newSelectedQuestionCategory = selectedQuestionCategoryId
        ? newQuestionCategories.find((questionCategory) => questionCategory.id === selectedQuestionCategoryId)
        : undefined
      form.change('questionCategory', newSelectedQuestionCategory)

      if (subjectGroup && INVERT_IMAGES_SUBJECT_GROUP_TITLES.includes(subjectGroup.title as CourseName)) {
        const invertFields = ['invertQuestionImages', 'invertAnswerImages'] as const
        invertFields.forEach((field) => {
          if (form.getFieldState(field)?.value !== QuestionInvertTypeEnum.NO) {
            form.change(field, QuestionInvertTypeEnum.YES)
          }
        })
      }
    },
    [getAvailableQuestionCategories]
  )

  const handleQuestionCategoryInputChange = useCallback(
    debounce((searchString: string, form: FormStateApi) => {
      const selectedSubjectGroup = form.getFieldState('subjectGroup')?.value
      const newQuestionCategories = getAvailableQuestionCategories(
        selectedSubjectGroup?.subjects.map(({ id }) => id) ?? [],
        searchString || undefined
      )
      setAvailableQuestionCategories(newQuestionCategories)

      const selectedQuestionCategoryId = form.getFieldState('questionCategory')?.value?.id
      const newSelectedQuestionCategory = selectedQuestionCategoryId
        ? newQuestionCategories.find((questionCategory) => questionCategory.id === selectedQuestionCategoryId)
        : undefined
      form.change('questionCategory', newSelectedQuestionCategory)
    }, DEBOUNCE_DELAY),
    [getAvailableQuestionCategories]
  )

  const handleLoadTemplate = useCallback(
    async (template: Question) => {
      const initialValues = await getInitialValues(question, template, undefined, allSubjectGroups)
      resetForm({
        ...initialValues,
        resetStatistics: Boolean(initialCorrectAnswerIds?.length),
      })
      setIsResetStatisticsDisabled(Boolean(initialCorrectAnswerIds?.length))
    },
    [question, initialCorrectAnswerIds, allSubjectGroups, resetForm]
  )

  const handleSave = useCallback(
    async (values: FormState, formApi: FormStateApi) => {
      setSubmitting(true)
      setIsModalOpen(false)
      try {
        const questionData = await prepareForSubmit(values)
        await onSubmit(questionData, formApi)
        setIsResetStatisticsDisabled(false)
      } catch (e) {
        toast.error(isImageUploadError(e) ? e.message : 'Failed to save question')
      }
      setSubmitting(false)
    },
    [onSubmit]
  )

  const handleSubmit = useCallback(
    async (values: FormState, formApi: FormStateApi) => {
      if (values.resetStatistics) {
        setIsModalOpen(true)
      } else {
        return handleSave(values, formApi)
      }
    },
    [handleSave]
  )

  const disabledPlatformIds = useMemo(
    () =>
      availablePlatforms
        ?.filter(({ title }) => questionLocations?.find(({ platformName }) => platformName === title))
        .map(({ id }) => id),
    [availablePlatforms, questionLocations]
  )

  const renderTitle = () => {
    if (isTemplate) {
      return 'Modify Question Templates'
    }

    return `${question ? 'Edit' : 'Create'} Question`
  }

  if (!initialValues) {
    return <PageLoader />
  }

  return (
    <div css={classes.wrapper}>
      <h1 css={classes.mainTitle}>{renderTitle()}</h1>
      <FinalForm<FormState>
        mutators={{ ...arrayMutators, setFieldData }}
        decorators={[focusOnErrors]}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, initialValues, submitting: formSubmitting, values, form }) => {
          const additionalFieldsConfig = getAdditionalFieldsConfig(values.type)
          const typeConfig = getQuestionTypeConfig(values.type)

          return (
            <form onSubmit={handleSubmit}>
              <OnChange name="type">{(questionType) => handleQuestionTypeChange(questionType, form)}</OnChange>
              <OnChange name="platforms">{(platforms) => handlePlatformsChange(platforms, form)}</OnChange>
              <OnChange name="subjectGroup">{(subjectGroup) => handleSubjectChange(subjectGroup, form)}</OnChange>

              <div css={classes.contentWrapper}>
                <div css={classes.content}>
                  {isTemplate && (
                    <InputField
                      name="templateName"
                      validate={validateRequired}
                      label="Template Name"
                      placeholder="Enter template name"
                      fullWidth
                      css={classes.templateName}
                    />
                  )}
                  <InputField
                    name="title"
                    validate={validateRequired}
                    label="Question Title"
                    placeholder="Enter Title"
                    fullWidth
                  />

                  <div css={classes.selectsWrapper}>
                    <MultiSelectField<Platform>
                      name="platforms"
                      validate={validateRequired}
                      disabledItemIds={disabledPlatformIds}
                      items={availablePlatforms}
                      renderItem={(platform) => getFullPlatformTitle(platform.title)}
                      getItemLabel={(platform) => getFullPlatformTitle(platform.title) ?? platform.title}
                      label="Platforms"
                      placeholder="Choose Platforms"
                      displaySelectedItemsFirst
                      changeImmediately
                      fullWidth
                    />
                    <SelectField<SubjectGroup>
                      name="subjectGroup"
                      validate={validateRequired}
                      items={availableSubjectGroups}
                      label="Subject"
                      placeholder="Choose Subject"
                      displaySelectedItemsFirst
                      fullWidth
                    />
                    <SelectField<QuestionCategory>
                      name="questionCategory"
                      validate={validateRequired}
                      items={availableQuestionCategories}
                      onInputChange={(searchString) => handleQuestionCategoryInputChange(searchString, form)}
                      label="Question Category"
                      placeholder="Choose Question Category"
                      displaySelectedItemsFirst
                      searchable
                      fullWidth
                    />
                  </div>

                  {!!additionalFieldsConfig && <AdditionalFields config={additionalFieldsConfig} values={values} />}

                  {values.type === QuestionTypeEnum.TestletNBDHE && (
                    <>
                      <div css={classes.title}>Testlet Scenario</div>

                      <EditorField
                        type="default"
                        name="testletScenario"
                        label="Testlet Scenario"
                        validate={validateRequiredHtml}
                        placeholder="Enter Testlet Scenario"
                        css={commonClasses.editor}
                      />
                    </>
                  )}

                  <div css={classes.title}>Question</div>

                  {typeConfig.questionInput === 'stem' ? (
                    <InputField
                      name="questionStem"
                      validate={validateRequired}
                      label="Question Stem"
                      placeholder="Enter Question Stem"
                      multiline
                      fullWidth
                    />
                  ) : (
                    <>
                      <EditorField
                        type="question"
                        name="questionAsHtml"
                        label="Question Details"
                        validate={validateRequiredHtml}
                        placeholder="Enter Question Details"
                        css={commonClasses.editor}
                      />
                      <InvertImagesField
                        name="invertQuestionImages"
                        title="Question Details"
                        validate={validateRequired}
                        htmlWithImagesForPreview={values.questionAsHtml}
                      />
                    </>
                  )}

                  {values.type === QuestionTypeEnum.RxWritingNDEB && (
                    <RxWritingAnswers onAnswerChange={() => onAnswerChange(form)} />
                  )}
                  {values.type === QuestionTypeEnum.NumericResponse && (
                    <NumericResponseAnswer onAnswerChange={() => onAnswerChange(form)} />
                  )}
                  {values.type === QuestionTypeEnum.TextCompletion && (
                    <TextCompletionAnswers onAnswerChange={() => onAnswerChange(form)} />
                  )}
                  {typeConfig.answerMode !== 'json' && (
                    <OptionChoices values={values} onAnswerChange={() => onAnswerChange(form)} />
                  )}

                  <div css={classes.title}>Solution</div>
                  <div css={classes.note}>
                    <b>Note:</b> If you want to use the platform name in the solution text, you can use the &quot;
                    {'{%PLATFORM_SHORT_NAME%}'}&quot; template, which will automatically be replaced with the short name
                    of the current platform when displayed to the user (e.g. &quot;DAT&quot; for DATBooster or
                    &quot;OAT&quot; for OATBooster). You can type this yourself or insert via &quot;Platform&quot;
                    button in Editor Toolbar.
                  </div>
                  <EditorField
                    type="solution"
                    name="answerExplanationAsHtml"
                    label="Solution Details"
                    validate={validateRequiredHtml}
                    placeholder="Enter Solution Details"
                    css={commonClasses.editor}
                  />
                  <InvertImagesField
                    name="invertAnswerExplanationImages"
                    title="Solution Details"
                    validate={validateRequired}
                    htmlWithImagesForPreview={values.answerExplanationAsHtml}
                  />

                  <div css={classes.sourcesAndCreditsWrapper}>
                    <AdditionalField config={SOURCES_AND_CREDITS_CONFIG} values={values} form={form} />
                  </div>

                  {!!initialValues.history?.length && (
                    <Revisions revisions={(initialValues?.history ?? []) as Revision[]} />
                  )}
                </div>

                <div css={classes.blocksWrapper}>
                  <div css={classes.block}>
                    <h4>Question Type</h4>
                    <SelectField<QuestionType>
                      name="questionType"
                      items={availableQuestionTypes}
                      placeholder="Choose Question Type"
                      disabled={!!question}
                      isNotNullable
                      fullWidth
                    />
                    <Button
                      onClick={() => {
                        form.change('type', values.questionType?.id)

                        const isNeedPatientBoxByDefault =
                          values.questionType?.id &&
                          getAdditionalFieldsConfig(values.questionType?.id)?.patientBoxNeededByDefault

                        if (isNeedPatientBoxByDefault) {
                          form.change('isNeedPatientBox', true)
                        }

                        if (values.questionType?.id === QuestionTypeEnum.RxWritingNDEB) {
                          form.change('questionAsHtml', '')
                        }
                      }}
                      variant="outlined"
                      color="blue"
                      type="button"
                      disabled={!!question}
                    >
                      Load Type
                    </Button>
                  </div>

                  {!isTemplate && (
                    <div css={classes.block}>
                      <TemplateForm
                        selectedQuestionType={values.type}
                        getTemplateData={(templateName) => prepareForSubmit(form.getState().values, templateName)}
                        setTemplateData={handleLoadTemplate}
                        isLoadTemplateDisabled={!!initialValues.id}
                      />
                    </div>
                  )}

                  <div css={classes.block}>
                    <h4>Publish</h4>
                    {!isTemplate && !!initialCorrectAnswerIds && (
                      <div>
                        <Field name="resetStatistics" type="checkbox">
                          {({ input }) => (
                            <FormControlLabel
                              label="Reset Statistics"
                              control={<Checkbox {...input} />}
                              disabled={isResetStatisticsDisabled}
                              css={classes.checkbox}
                            />
                          )}
                        </Field>
                      </div>
                    )}
                    <Button type="submit" variant="contained" color="blue" disabled={formSubmitting || submitting}>
                      {isTemplate ? 'Save' : 'Publish'}
                    </Button>
                    {!!initialValues.id && !disableDeleteButton && (
                      <RemoveButton
                        questionId={initialValues?.id ?? ''}
                        submitting={formSubmitting || submitting}
                        setSubmitting={setSubmitting}
                        setRemoveError={setRemoveError}
                        isTemplate={isTemplate}
                      />
                    )}
                    {removeError && (
                      <Typography variant="body2" css={classes.badge(theme.colors.red)}>
                        The question is used either in a Question bank or a Practice test or a Video quiz
                      </Typography>
                    )}
                  </div>

                  {!!initialValues.id && !isTemplate && (
                    <div css={classes.block}>
                      <h4>Question Location</h4>
                      {questionLocations?.length ? (
                        <div css={classes.questionLocations}>
                          {questionLocations?.map(({ locationId, locationName, platformName }) => (
                            // TODO: how to resolve platform color?
                            <Badge
                              key={platformName + locationId}
                              text={`${platformName} > ${locationName}`}
                              color="#2489FF"
                            />
                          ))}
                        </div>
                      ) : (
                        <div css={classes.empty}>Nothing found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <ConfirmationModal
                open={isModalOpen}
                title="Reset Statistics"
                subtitle={
                  <>
                    Are you sure you want to reset the statistics for <br />
                    this question? This action cannot be changed.
                  </>
                }
                onClose={() => setIsModalOpen(false)}
                onConfirm={() => handleSave(form.getState().values, form)}
                confirmText="Confirm Reset & Publish"
                closeText="Cancel"
              />
            </form>
          )
        }}
      />
    </div>
  )
}
